import Box from "@mui/material/Box"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { useMemo, useState } from "react"

import MuiAutoComplete from "components/MuiAutocomplete"
import MuiModal from "components/MuiModal"
import SearchInputV2 from "components/SearchInputV2/SearchInputV2"
import { CHARGER_TYPE, ITEM_CATEGORY } from "enums/dropdownValues"
import { useGetOEMQuery, useGetProductsQuery } from "pages/Common/commonService"
import { tableCellStyles, tableHeaderCellStyles } from "./index"

type Props = {
    open: boolean
    onClose: () => void
    type: "charger" | "material"
}

type DropdownOption = { id: string | number; title: string } | null

const SkusListModal = ({ open, onClose, type }: Props) => {
    const isCharger = type === "charger"

    const { data: products } = useGetProductsQuery(isCharger ? { is_charger: 1 } : { is_material: 1 })
    const { data: oemList } = useGetOEMQuery()

    const [search, setSearch] = useState("")
    const [chargerTypeFilter, setChargerTypeFilter] = useState<DropdownOption>(null)
    const [oemFilter, setOemFilter] = useState<DropdownOption>(null)
    const [categoryFilter, setCategoryFilter] = useState<DropdownOption>(null)

    const oemOptions = useMemo(() => oemList?.data?.map(o => ({ id: o.id, title: o.name })) || [], [oemList])

    const oemMap = useMemo(() => {
        const map: Record<number, string> = {}
        oemList?.data?.forEach(o => {
            map[o.id] = o.name
        })
        return map
    }, [oemList])

    const chargerTypeOptions = useMemo(() => [{ id: "all", title: "All" }, ...CHARGER_TYPE], [])

    const filteredProducts = useMemo(() => {
        const list = products || []
        const searchKey = search.trim().toLowerCase()
        return list.filter(p => {
            if (searchKey) {
                const matches =
                    p.sku_code?.toLowerCase().includes(searchKey) ||
                    p.item_description?.toLowerCase().includes(searchKey)
                if (!matches) return false
            }
            if (isCharger) {
                if (chargerTypeFilter && chargerTypeFilter.id !== "all") {
                    if ((p.charger_type || "").toLowerCase() !== String(chargerTypeFilter.id).toLowerCase()) {
                        return false
                    }
                }
            } else {
                if (categoryFilter && p.category !== categoryFilter.id) return false
            }
            if (oemFilter && p.oem_id !== oemFilter.id) return false
            return true
        })
    }, [products, search, chargerTypeFilter, oemFilter, categoryFilter, isCharger])

    const handleClose = () => {
        setSearch("")
        setChargerTypeFilter(null)
        setOemFilter(null)
        setCategoryFilter(null)
        onClose()
    }

    const columns = isCharger
        ? [
              { id: "sku_code", label: "SKU Code", width: 140 },
              { id: "display_name", label: "Display Name", width: 280 },
              { id: "charger_type", label: "Charger Type", width: 120 },
              { id: "charger_power_rating", label: "Power Rating", width: 130 },
              { id: "oem", label: "OEM", width: 160 },
          ]
        : [
              { id: "sku_code", label: "SKU Code", width: 140 },
              { id: "display_name", label: "Display Name", width: 320 },
              { id: "oem", label: "OEM", width: 160 },
              { id: "category", label: "Item Category", width: 160 },
          ]

    return (
        <MuiModal width="45vw" open={open} onClose={handleClose} header={isCharger ? "Charger SKUs" : "Material SKUs"}>
            <Box display="flex" flexDirection="column" gap={2} padding={"var(--mui-modal-padding)"}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <Box flex={1} minWidth={240}>
                        <SearchInputV2
                            width="100%"
                            placeholder="Search by SKU or Description"
                            value={search}
                            onSearch={(val: string) => setSearch(val ?? "")}
                            searchIcon
                        />
                    </Box>
                    {isCharger ? (
                        <Box minWidth={160}>
                            <MuiAutoComplete
                                disablePortal={false}
                                value={chargerTypeFilter}
                                fullWidth
                                options={chargerTypeOptions}
                                placeholder="Charger Type"
                                onChange={(v: any) => setChargerTypeFilter(v)}
                            />
                        </Box>
                    ) : (
                        <Box minWidth={180}>
                            <MuiAutoComplete
                                disablePortal={false}
                                value={categoryFilter}
                                fullWidth
                                options={ITEM_CATEGORY}
                                placeholder="Item Category"
                                onChange={(v: any) => setCategoryFilter(v)}
                            />
                        </Box>
                    )}
                    <Box minWidth={180}>
                        <MuiAutoComplete
                            disablePortal={false}
                            value={oemFilter}
                            fullWidth
                            options={oemOptions}
                            placeholder="OEM"
                            onChange={(v: any) => setOemFilter(v)}
                        />
                    </Box>
                    <Typography
                        onClick={() => {
                            setSearch("")
                            setChargerTypeFilter(null)
                            setOemFilter(null)
                            setCategoryFilter(null)
                        }}
                        variant="label"
                        sx={{
                            color: "#FC5735",
                            fontWeight: 500,
                            fontSize: "12px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                        }}>
                        Reset
                    </Typography>
                </Box>

                <TableContainer className="custom-scrollbar" sx={{ maxHeight: "55vh", overflowY: "auto" }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell
                                        key={`sku-header-${col.id}`}
                                        sx={{ ...tableHeaderCellStyles, width: col.width }}>
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredProducts.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        sx={{
                                            ...tableCellStyles,
                                            textAlign: "center",
                                            color: "#70758D",
                                            padding: "12px",
                                        }}>
                                        No SKUs found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredProducts.map((p, rowIndex) => (
                                    <TableRow key={`sku-row-${p.id ?? rowIndex}`}>
                                        {columns.map(col => {
                                            let content: React.ReactNode = "-"
                                            if (col.id === "sku_code") content = p.sku_code || "-"
                                            else if (col.id === "display_name") content = p.display_name || "-"
                                            else if (col.id === "charger_type")
                                                content = (
                                                    <Box sx={{ textTransform: "uppercase", padding: "4px 8px" }}>
                                                        {p.charger_type || "-"}
                                                    </Box>
                                                )
                                            else if (col.id === "charger_power_rating")
                                                content = p.charger_power_rating ? `${p.charger_power_rating} kWh` : "-"
                                            else if (col.id === "oem") content = (p.oem_id && oemMap[p.oem_id]) || "-"
                                            else if (col.id === "category") content = p.category || "-"
                                            return (
                                                <TableCell
                                                    key={`sku-cell-${p.id ?? rowIndex}-${col.id}`}
                                                    sx={{
                                                        ...tableCellStyles,
                                                        width: col.width,
                                                        padding: "4px 8px",
                                                    }}>
                                                    {content}
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </MuiModal>
    )
}

export default SkusListModal
